import * as actionTypes from 'modules/audits/actionTypes';
import type { PayloadAction } from 'modules/global/generic.types';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { createReducer } from 'utils/ReduxUtils';

/** Who an audit is assigned to, as ids - the header resolves the labels from the user/group stores. */
export type AuditAssignment = {
  userId: string | null;
  userGroupId: string | null;
};

export type AuditAssignmentByAuditId = Record<string, AuditAssignment>;

type AssignmentPayload = {
  id: string;
  assignedUser?: { id: string } | null;
  assignedUserGroup?: { id: string } | null;
};

/** Shared reference so a component reading an audit that has never been fetched does not re-render. */
export const EMPTY_ASSIGNMENT: AuditAssignment = {
  userId: null,
  userGroupId: null,
};

export const initialState: AuditAssignmentByAuditId = {};

const setAssignment = (
  state: AuditAssignmentByAuditId,
  { payload }: PayloadAction<AssignmentPayload>,
): AuditAssignmentByAuditId => ({
  ...state,
  [payload.id]: {
    userId: payload.assignedUser?.id ?? null,
    userGroupId: payload.assignedUserGroup?.id ?? null,
  },
});

const removeAssignment = (
  state: AuditAssignmentByAuditId,
  { payload: auditId }: PayloadAction<string>,
): AuditAssignmentByAuditId => {
  const { [auditId]: _removed, ...rest } = state;
  return rest;
};

const reset = (): AuditAssignmentByAuditId => initialState;

// Every single-audit success rebuilds the entry from the response: the unassign response omits the key
// it did not set, so both are normalized to null rather than merged into the previous value.
const successHandlers = Object.fromEntries(
  actionTypes.SINGLE_AUDIT_SUCCESS_TYPES.map(type => [type, setAssignment] as const),
);

export default createReducer<AuditAssignmentByAuditId>(initialState, {
  ...successHandlers,
  [actionTypes.DELETE_SUCCESS]: removeAssignment,
  [LOCATION_CHANGE]: reset,
});
